import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ShieldCheck, AlertCircle, Smartphone, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

const METHODS = {
  totp: { label: "Authenticator app", icon: Smartphone, placeholder: "123456", hint: "Enter the 6-digit code from your authenticator app." },
  email: { label: "Email code", icon: Mail, placeholder: "123456", hint: "Enter the 6-digit code we emailed you." },
  backup: { label: "Backup code", icon: KeyRound, placeholder: "XXXX-XXXX", hint: "Enter one of your single-use backup codes." },
};

const MfaChallenge = () => {
  const { mfaState, verifyMfa, sendEmailOtp } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (!mfaState) navigate("/login", { replace: true });
  }, [mfaState, navigate]);

  if (!mfaState) {
    return null;
  }

  const availableMethods = mfaState.methods || ["totp", "backup"];

  const switchMethod = (m) => {
    setMethod(m);
    setCode("");
    setError("");
  };

  const handleSendEmail = async () => {
    setError("");
    try {
      await sendEmailOtp();
      setEmailSent(true);
      toast.success(`Code sent to ${mfaState.emailMasked || "your email"}`);
    } catch (err) {
      const message = err.response?.data?.detail || "Could not send email code.";
      setError(typeof message === "string" ? message : "Could not send email code.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await verifyMfa(code.trim(), method);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate(user.role === "account_manager" ? "/admin/portal" : user.role === "vendor" ? "/vendor" : "/employee");
    } catch (err) {
      const message = err.response?.data?.detail || "Invalid code. Please try again.";
      setError(typeof message === "string" ? message : "Invalid code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cfg = METHODS[method];

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 bg-emerald-900 rounded-lg flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-emerald-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Two-factor verification
          </span>
        </div>

        <Card className="border-stone-200 shadow-lg" data-testid="mfa-challenge-card">
          <CardHeader>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
              Verify it's you
            </CardTitle>
            <CardDescription>{cfg.hint}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="mfa-challenge-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">{cfg.label}</Label>
                <Input
                  id="mfa-code"
                  placeholder={cfg.placeholder}
                  value={code}
                  onChange={(e) => setCode(method === "backup" ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
                  maxLength={method === "backup" ? 9 : 6}
                  inputMode={method === "backup" ? "text" : "numeric"}
                  className="text-center text-lg tracking-[0.3em]"
                  required
                  data-testid="mfa-challenge-code-input"
                />
              </div>
              {method === "email" && (
                <Button type="button" variant="outline" className="w-full" onClick={handleSendEmail} data-testid="mfa-send-email-btn">
                  {emailSent ? "Resend email code" : `Send code to ${mfaState.emailMasked || "my email"}`}
                </Button>
              )}
              <Button type="submit" className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full h-11"
                disabled={submitting || !code} data-testid="mfa-challenge-verify-btn">
                {submitting ? "Verifying…" : "Verify"}
              </Button>
            </form>

            <div className="border-t border-stone-200 pt-4 space-y-2">
              <p className="text-xs text-stone-500">Use a different method</p>
              <div className="flex flex-wrap gap-2">
                {availableMethods.filter((m) => m !== method).map((m) => {
                  const Icon = METHODS[m].icon;
                  return (
                    <Button key={m} type="button" variant="outline" size="sm" onClick={() => switchMethod(m)}
                      data-testid={`mfa-method-${m}-btn`}>
                      <Icon className="w-3.5 h-3.5 mr-1.5" /> {METHODS[m].label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MfaChallenge;
